import { useCallback, useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Space, Typography, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { ImageWithPreview, showErrorToast } from '@/components';
import { MAX_IMAGE_SIZE } from '@/constants/files';
import { formatApiError, validateFileSize } from '@/utils';
import { BLOG_IMAGE_ACCEPT, resolveBlogMediaDisplayUrl, uploadBlogImageFile } from '../../helpers/blogMedia';
import { StyledBlogImagePreviewShell } from './BlogImageFieldStyles';

type TBlogImageFieldProps = {
  value: unknown;
  onChange: (v: string) => void;
  /** Optional helper text (e.g. format & size rules for big_banner). */
  requirementsText?: string;
};

const BlogImageField = ({ value, onChange, requirementsText }: TBlogImageFieldProps) => {
  const { t } = useTranslation(['contentPages']);
  const [uploading, setUploading] = useState(false);
  const url = typeof value === 'string' ? value : '';

  const handleBeforeUpload = useCallback(
    (file: File) => {
      if (!validateFileSize(file.size, MAX_IMAGE_SIZE)) {
        showErrorToast({
          message: t('error_image_too_large', { limit: MAX_IMAGE_SIZE / 1024 / 1024 }),
        });
        return false;
      }
      setUploading(true);
      void (async () => {
        try {
          const nextUrl = await uploadBlogImageFile(file);
          onChange(nextUrl);
        } catch (e) {
          showErrorToast({ message: formatApiError(e).message });
        } finally {
          setUploading(false);
        }
      })();
      return false;
    },
    [onChange, t],
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {requirementsText ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12, lineHeight: 1.5 }}>
          {requirementsText}
        </Typography.Paragraph>
      ) : null}
      {url ? (
        <StyledBlogImagePreviewShell>
          <ImageWithPreview src={resolveBlogMediaDisplayUrl(url)} alt="" />
        </StyledBlogImagePreviewShell>
      ) : null}
      <Upload.Dragger
        beforeUpload={handleBeforeUpload}
        accept={BLOG_IMAGE_ACCEPT}
        showUploadList={false}
        multiple={false}
        disabled={uploading}
        style={{ padding: '12px 8px' }}
      >
        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
          <InboxOutlined style={{ fontSize: 36, color: '#1677ff' }} />
        </p>
        <p className="ant-upload-text" style={{ marginBottom: 4 }}>
          {t('image_drop_title')}
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
          {t('image_drop_hint')}
        </p>
      </Upload.Dragger>
    </Space>
  );
};

export default BlogImageField;
